(() => {
  'use strict';

  const INITIALIZED = new WeakSet();
  let predictiveClassPatched = false;

  function escapeHTML(value) {
    const el = document.createElement('div');
    el.textContent = String(value ?? '');
    return el.innerHTML;
  }

  function debounce(callback, delay = 250) {
    let timer;

    return (...args) => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        callback(...args);
      }, delay);
    };
  }

  function normalizeISBN(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '');
  }

  function getISBNFromQuery(value) {
    const query = String(value || '').trim();

    if (!query) return null;

    /*
     * Supports:
     * 123456
     * 9781234567890
     * 978-1-23456-789-0
     * 123456789X
     */
    if (!/^[0-9Xx-]+$/.test(query)) {
      return null;
    }

    const cleaned = query.replace(/[^0-9Xx]/g, '');

    /*
     * Allows test ISBN 123456
     */
    if (cleaned.length < 5) {
      return null;
    }

    return query;
  }

  function getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  /*
   * ==========================================================
   * FETCH PRODUCT USING custom.isbn13
   * ==========================================================
   */

  async function searchISBN13Metafield(isbn) {
    const params = new URLSearchParams();

    params.set(
      'filter.p.m.custom.isbn13',
      isbn
    );

    params.set(
      'section_id',
      'isbn13-search-data'
    );

    const url =
      `${getRoot()}collections/all?${params.toString()}`;

    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `ISBN13 search failed: ${response.status}`
      );
    }

    const html = await response.text();

    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const dataElement =
      doc.querySelector(
        '[data-isbn13-search-data]'
      );

    if (!dataElement) {
      throw new Error(
        'isbn13-search-data section not found'
      );
    }

    let products = [];

    try {
      products = JSON.parse(
        dataElement.textContent.trim()
      );
    } catch (error) {
      console.error(
        '[ISBN13] Invalid JSON',
        error
      );

      return [];
    }

    if (!Array.isArray(products)) {
      return [];
    }

    const requested =
      normalizeISBN(isbn);

    return products.filter((product) => {
      return (
        normalizeISBN(
          product.isbn13
        ) === requested
      );
    });
  }

  /*
   * ==========================================================
   * DROPDOWN HELPERS
   * ==========================================================
   */

  function removeISBNDropdownResults(
    predictiveSearch
  ) {
    predictiveSearch
      .querySelectorAll(
        '[data-isbn-results]'
      )
      .forEach((el) => {
        el.remove();
      });
  }

  function productAlreadyExists(
    predictiveSearch,
    product
  ) {
    if (!product?.url) return false;

    let targetPath;

    try {
      targetPath =
        new URL(
          product.url,
          window.location.origin
        ).pathname;
    } catch {
      return false;
    }

    return Array.from(
      predictiveSearch.querySelectorAll(
        'a[href]'
      )
    ).some((link) => {
      if (
        link.closest(
          '[data-isbn-results]'
        )
      ) {
        return false;
      }

      try {
        return (
          new URL(
            link.href,
            window.location.origin
          ).pathname === targetPath
        );
      } catch {
        return false;
      }
    });
  }

  function createDropdownProduct(
    product,
    isbn,
    index
  ) {
    const li =
      document.createElement('li');

    li.id =
      `isbn-result-${index}`;

    li.setAttribute(
      'role',
      'option'
    );

    li.setAttribute(
      'aria-selected',
      'false'
    );

    const image =
      product.image
        ? `
          <img
            class="predictive-search__image"
            src="${escapeHTML(product.image)}"
            alt="${escapeHTML(product.title)}"
            width="70"
            height="70"
            loading="lazy"
          >
        `
        : '';

    li.innerHTML = `
      <a
        href="${escapeHTML(product.url)}"
        class="
          predictive-search__item
          predictive-search__item--link-with-thumbnail
          link
          link--text
        "
        tabindex="-1"
      >

        ${image}

        <div
          class="
            predictive-search__item-content
            predictive-search__item-content--centered
          "
        >

          <p
            class="predictive-search__item-heading h5"
          >
            ${escapeHTML(product.title)}
          </p>

          <span
            style="
              display:block;
              margin-top:3px;
              font-size:12px;
              opacity:.65;
            "
          >
            ISBN: ${escapeHTML(isbn)}
          </span>

          ${
            product.price
              ? `
                <div
                  class="price"
                  style="margin-top:5px;"
                >
                  <span
                    class="price-item price-item--regular"
                  >
                    ${escapeHTML(product.price)}
                  </span>
                </div>
              `
              : ''
          }

        </div>

      </a>
    `;

    return li;
  }

  function injectISBNDropdownResults(
    predictiveSearch,
    products,
    isbn
  ) {
    removeISBNDropdownResults(
      predictiveSearch
    );

    if (
      !Array.isArray(products) ||
      !products.length
    ) {
      return;
    }

    const container =
      predictiveSearch.querySelector(
        '[data-predictive-search]'
      );

    if (!container) return;

    const renderProducts =
      products.filter(
        (product) =>
          !productAlreadyExists(
            predictiveSearch,
            product
          )
      );

    if (!renderProducts.length) {
      return;
    }

    const group =
      document.createElement('div');

    group.className =
      'predictive-search__result-group';

    group.setAttribute(
      'data-isbn-results',
      ''
    );

    const inner =
      document.createElement('div');

    const heading =
      document.createElement('h2');

    heading.className =
      'predictive-search__heading caption-with-letter-spacing';

    heading.textContent =
      'Products';

    const list =
      document.createElement('ul');

    list.className =
      'predictive-search__results-list list-unstyled';

    list.setAttribute(
      'role',
      'group'
    );

    renderProducts.forEach(
      (product, index) => {
        list.appendChild(
          createDropdownProduct(
            product,
            isbn,
            index
          )
        );
      }
    );

    inner.appendChild(heading);
    inner.appendChild(list);
    group.appendChild(inner);

    let wrapper =
      predictiveSearch.querySelector(
        '#predictive-search-results-groups-wrapper'
      );

    if (!wrapper) {
      wrapper =
        document.createElement('div');

      wrapper.id =
        'predictive-search-results-groups-wrapper';

      wrapper.className =
        'predictive-search__results-groups-wrapper';

      const results =
        container.querySelector(
          '#predictive-search-results'
        );

      if (results) {
        results.prepend(wrapper);
      } else {
        container.prepend(wrapper);
      }
    }

    wrapper.prepend(group);

    predictiveSearch.setAttribute(
      'results',
      'true'
    );

    if (
      typeof predictiveSearch.open ===
      'function'
    ) {
      predictiveSearch.open();
    }
  }

  async function runDropdownISBN(
    predictiveSearch,
    input,
    isbn,
    token
  ) {
    const products =
      await searchISBN13Metafield(
        isbn
      );

    if (
      predictiveSearch.__isbnToken !==
      token
    ) {
      return;
    }

    const currentISBN =
      getISBNFromQuery(
        input.value
      );

    if (!currentISBN) return;

    if (
      normalizeISBN(currentISBN) !==
      normalizeISBN(isbn)
    ) {
      return;
    }

    predictiveSearch.__isbnProducts =
      products;

    predictiveSearch.__isbnValue =
      isbn;

    injectISBNDropdownResults(
      predictiveSearch,
      products,
      isbn
    );
  }

  /*
   * ==========================================================
   * SEARCH RESULTS PAGE PRODUCT CARD
   * ==========================================================
   */

  function createSearchPageProductCard(
    product,
    isbn
  ) {
    const imageMarkup =
      product.image
        ? `
          <div
            class="card__media"
          >
            <div
              class="media media--transparent media--hover-effect"
            >
              <img
                src="${escapeHTML(product.image)}"
                alt="${escapeHTML(product.title)}"
                loading="lazy"
                width="600"
                height="600"
              >
            </div>
          </div>
        `
        : '';

    return `
      <li class="grid__item">

        <div
          class="
            card-wrapper
            product-card-wrapper
            underline-links-hover
          "
        >

          <div
            class="
              card
              card--standard
              card--media
            "
          >

            <div
              class="
                card__inner
                color-scheme-2
                gradient
                ratio
              "
              style="--ratio-percent: 100%;"
            >

              ${imageMarkup}

              <div class="card__content">

                <div class="card__information">

                  <h3
                    class="card__heading"
                  >
                    <a
                      href="${escapeHTML(product.url)}"
                      class="full-unstyled-link"
                    >
                      ${escapeHTML(product.title)}
                    </a>
                  </h3>

                </div>

              </div>

            </div>

            <div class="card__content">

              <div class="card__information">

                <h3
                  class="card__heading h5"
                >
                  <a
                    href="${escapeHTML(product.url)}"
                    class="full-unstyled-link"
                  >
                    ${escapeHTML(product.title)}
                  </a>
                </h3>

                <div class="card-information">

                  <span
                    class="caption-large light"
                  >
                    ISBN:
                    ${escapeHTML(isbn)}
                  </span>

                  ${
                    product.price
                      ? `
                        <div
                          class="price"
                          style="margin-top:6px;"
                        >
                          <div class="price__container">
                            <div class="price__regular">
                              <span
                                class="price-item price-item--regular"
                              >
                                ${escapeHTML(product.price)}
                              </span>
                            </div>
                          </div>
                        </div>
                      `
                      : ''
                  }

                </div>

              </div>

            </div>

          </div>

        </div>

      </li>
    `;
  }

  /*
   * ==========================================================
   * RENDER ISBN PRODUCTS ON /search PAGE
   * ==========================================================
   */

  async function renderISBN13SearchPage() {
    const url =
      new URL(
        window.location.href
      );

    const cleanPath =
      url.pathname.replace(
        /\/+$/,
        ''
      );

    if (
      !cleanPath.endsWith(
        '/search'
      )
    ) {
      return;
    }

    const isbn =
      url.searchParams.get(
        'isbn13'
      );

    if (!isbn) {
      return;
    }

    /*
     * Make sure search input displays ISBN,
     * not any internal query.
     */
    document
      .querySelectorAll(
        'input[type="search"]'
      )
      .forEach((input) => {
        input.value = isbn;
      });

    let products = [];

    try {
      products =
        await searchISBN13Metafield(
          isbn
        );
    } catch (error) {
      console.error(
        '[ISBN13 Search Page]',
        error
      );

      return;
    }

    if (!products.length) {
      return;
    }

    /*
     * Remove Dawn's "empty search" spacing.
     */
    document
      .querySelector(
        '.template-search'
      )
      ?.classList.remove(
        'template-search--empty'
      );

    /*
     * Hide Dawn native empty result status
     * if metafield match was found.
     */
    document
      .querySelectorAll(
        '.template-search__header p[role="status"]'
      )
      .forEach((status) => {
        status.style.display =
          'none';
      });

    /*
     * Hide incorrect native search product count.
     */
    document
      .querySelectorAll(
        '.product-count-vertical'
      )
      .forEach((count) => {
        count.style.display =
          'none';
      });

    const gridContainer =
      document.querySelector(
        '#ProductGridContainer'
      );

    if (!gridContainer) {
      console.error(
        '[ISBN13] #ProductGridContainer not found'
      );

      return;
    }

    const cards =
      products
        .map((product) =>
          createSearchPageProductCard(
            product,
            isbn
          )
        )
        .join('');

    /*
     * Same structure Dawn uses for
     * normal search products.
     */
    gridContainer.innerHTML = `
      <div
        class="template-search__results collection page-width"
        id="product-grid"
        data-id="isbn13-search"
      >

        <div
          class="loading-overlay gradient"
        ></div>

        <ul
          class="
            grid
            product-grid
            grid--2-col-tablet-down
            grid--4-col-desktop
          "
          role="list"
        >

          ${cards}

        </ul>

      </div>
    `;
  }

  /*
   * ==========================================================
   * INITIALIZE EACH DAWN PREDICTIVE SEARCH
   * ==========================================================
   */

  function initializePredictiveSearch(
    predictiveSearch
  ) {
    if (
      INITIALIZED.has(
        predictiveSearch
      )
    ) {
      return;
    }

    INITIALIZED.add(
      predictiveSearch
    );

    const input =
      predictiveSearch.querySelector(
        'input[type="search"]'
      );

    if (!input) return;

    predictiveSearch.__isbnToken =
      0;

    predictiveSearch.__isbnProducts =
      [];

    predictiveSearch.__isbnValue =
      null;

    const handleInput =
      debounce(() => {
        const isbn =
          getISBNFromQuery(
            input.value
          );

        predictiveSearch.__isbnToken++;

        const token =
          predictiveSearch.__isbnToken;

        removeISBNDropdownResults(
          predictiveSearch
        );

        predictiveSearch.__isbnProducts =
          [];

        predictiveSearch.__isbnValue =
          null;

        /*
         * Normal text search:
         * do absolutely nothing.
         * Dawn handles it.
         */
        if (!isbn) {
          return;
        }

        runDropdownISBN(
          predictiveSearch,
          input,
          isbn,
          token
        ).catch((error) => {
          console.error(
            '[ISBN13 Dropdown]',
            error
          );
        });

      }, 250);

    input.addEventListener(
      'input',
      handleInput
    );

    /*
     * ========================================================
     * ISBN FORM SUBMIT
     *
     * Customer:
     * 123456
     *
     * URL:
     * /search?q=123456&type=product&isbn13=123456
     * ========================================================
     */

    input.form?.addEventListener(
      'submit',
      (event) => {
        const isbn =
          getISBNFromQuery(
            input.value
          );

        if (!isbn) {
          return;
        }

        /*
         * If customer selected a predictive result
         * with keyboard, Dawn handles it normally.
         */
        const selected =
          predictiveSearch.querySelector(
            '[aria-selected="true"] a'
          );

        if (selected) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const params =
          new URLSearchParams();

        params.set(
          'q',
          isbn
        );

        params.set(
          'type',
          'product'
        );

        /*
         * Flag used by search page renderer.
         */
        params.set(
          'isbn13',
          isbn
        );

        window.location.href =
          `${getRoot()}search?${params.toString()}`;
      },
      true
    );
  }

  /*
   * ==========================================================
   * DAWN RE-RENDER PATCH
   * ==========================================================
   */

  function patchDawnPredictiveSearch() {
    if (
      predictiveClassPatched
    ) {
      return true;
    }

    const PredictiveSearchClass =
      customElements.get(
        'predictive-search'
      );

    if (!PredictiveSearchClass) {
      return false;
    }

    const originalRender =
      PredictiveSearchClass
        .prototype
        .renderSearchResults;

    if (
      typeof originalRender !==
      'function'
    ) {
      return false;
    }

    PredictiveSearchClass
      .prototype
      .renderSearchResults =
      function (
        resultsMarkup
      ) {
        /*
         * Original Dawn render.
         */
        originalRender.call(
          this,
          resultsMarkup
        );

        const products =
          this.__isbnProducts;

        const isbn =
          this.__isbnValue;

        /*
         * Re-add metafield results after Dawn
         * replaces predictive search markup.
         */
        if (
          Array.isArray(products) &&
          products.length &&
          isbn
        ) {
          requestAnimationFrame(
            () => {
              injectISBNDropdownResults(
                this,
                products,
                isbn
              );
            }
          );
        }
      };

    predictiveClassPatched =
      true;

    return true;
  }

  function initializeAll() {
    document
      .querySelectorAll(
        'predictive-search'
      )
      .forEach(
        initializePredictiveSearch
      );
  }

  function boot() {
    initializeAll();

    /*
     * Search page ISBN renderer.
     */
    renderISBN13SearchPage();

    /*
     * Wait until Dawn predictive-search
     * custom element is registered.
     */
    if (
      !patchDawnPredictiveSearch()
    ) {
      let attempts = 0;

      const timer =
        setInterval(() => {
          attempts++;

          if (
            patchDawnPredictiveSearch() ||
            attempts >= 50
          ) {
            clearInterval(timer);
          }
        }, 100);
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }

  /*
   * Handles dynamically inserted
   * search drawer / search elements.
   */
  const observer =
    new MutationObserver(() => {
      initializeAll();
    });

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();